import { createRoute, Navigate, Outlet } from "@tanstack/react-router";
import { BuyerDetailsPage } from "pages/app/BuyerDetailsPage";
import { BuyersPage } from "pages/app/BuyersPage";
import { BuyerSupplierRelationshipPage } from "pages/app/SupplierBuyerRelationshipPage";

import { DetailsPage } from "components/app_layout/DetailsLayout";
import { BuyerDetailLayout, useBuyerFromRoute } from "components/buyer_details/BuyerDetailLayout";
import { BuyerList } from "components/buyers/BuyerList";
import BuyerLists from "components/buyers/BuyerLists";
import { rootRoute } from "../rootRoute";

// ---------------------------------------------------------------------------
// /buyers layout — groups all buyer pages under one prefix
// ---------------------------------------------------------------------------

export const buyersLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/buyers",
  staticData: { pageName: "", breadcrumbLabel: "Buyers" },
  component: Outlet,
});

// /buyers/ → redirect to /buyers/all-buyers
export const buyersIndexRoute = createRoute({
  getParentRoute: () => buyersLayoutRoute,
  path: "/",
  staticData: { pageName: "Find buyers" },
  component: function BuyersIndexRedirect() {
    return <Navigate to="/buyers/all-buyers" replace />;
  },
});

// /buyers/all-buyers
export const buyersAllRoute = createRoute({
  getParentRoute: () => buyersLayoutRoute,
  path: "/all-buyers",
  staticData: { pageName: "Find buyers" },
  component: BuyersPage,
});

// /buyers/lists
export const buyersListsRoute = createRoute({
  getParentRoute: () => buyersLayoutRoute,
  path: "/lists",
  staticData: { pageName: "Buyer lists", breadcrumbLabel: "Lists" },
  component: function BuyersListsRouteComp() {
    return (
      <DetailsPage>
        <BuyerLists />
      </DetailsPage>
    );
  },
});

// /buyers/lists/$listId
export const buyersListDetailRoute = createRoute({
  getParentRoute: () => buyersLayoutRoute,
  path: "/lists/$listId",
  staticData: { pageName: "Buyer list" },
  component: function BuyersListDetailRouteComp() {
    const { listId } = buyersListDetailRoute.useParams();
    return (
      <DetailsPage>
        <BuyerList id={listId} />
      </DetailsPage>
    );
  },
});

// ---------------------------------------------------------------------------
// /buyers/$id layout — wraps all buyer detail sub-pages with shared data fetching
// ---------------------------------------------------------------------------

export const buyerIdLayoutRoute = createRoute({
  getParentRoute: () => buyersLayoutRoute,
  path: "/$id",
  staticData: { pageName: "" },
  component: BuyerDetailLayout,
});

// /buyers/$id/supplier/$supplierGuid
export const buyerSupplierRoute = createRoute({
  getParentRoute: () => buyerIdLayoutRoute,
  path: "/supplier/$supplierGuid",
  staticData: { pageName: "Buyer supplier relationship" },
  component: function BuyerSupplierRouteComp() {
    const { supplierGuid } = buyerSupplierRoute.useParams();
    const buyer = useBuyerFromRoute();
    if (!buyer) return null;
    return <BuyerSupplierRelationshipPage buyer={buyer} supplierGuid={supplierGuid} />;
  },
});

// /buyers/$id/partner/$signalId — legacy alias → redirects to the buyer's partners tab
export const buyerPartnerRoute = createRoute({
  getParentRoute: () => buyerIdLayoutRoute,
  path: "/partner/$signalId",
  staticData: { pageName: "" },
  component: function BuyerPartnerLegacyRedirect() {
    const { id } = buyerPartnerRoute.useParams();
    return <Navigate to="/buyers/$id/$" params={{ id, _splat: "suppliers/partners" }} replace />;
  },
});

// /buyers/$id/competitor/$signalId — legacy alias → redirects to the buyer's competitors tab
export const buyerCompetitorRoute = createRoute({
  getParentRoute: () => buyerIdLayoutRoute,
  path: "/competitor/$signalId",
  staticData: { pageName: "" },
  component: function BuyerCompetitorLegacyRedirect() {
    const { id } = buyerCompetitorRoute.useParams();
    return <Navigate to="/buyers/$id/$" params={{ id, _splat: "suppliers/competitors" }} replace />;
  },
});

// /buyers/$id/$ (catch-all for tab paths like /contacts, /activity, etc.)
// pageName deliberately empty so the router-level `tracking.pageView` subscriber skips it —
// BuyerDetailsPage emits its own "Buyer Profile" page view (with Buyer name/ID). Matches
// the supplierDetailRoute convention and prevents duplicate Amplitude events per load.
export const buyerDetailRoute = createRoute({
  getParentRoute: () => buyerIdLayoutRoute,
  path: "/$",
  staticData: { pageName: "" },
  component: BuyerDetailsPage,
});

// ---------------------------------------------------------------------------
// Route tree export
// ---------------------------------------------------------------------------

export const buyersRouteTree = buyersLayoutRoute.addChildren([
  buyersIndexRoute,
  buyersAllRoute,
  buyersListsRoute,
  buyersListDetailRoute,
  buyerIdLayoutRoute.addChildren([
    buyerSupplierRoute,
    buyerPartnerRoute,
    buyerCompetitorRoute,
    buyerDetailRoute,
  ]),
]);
